import { useMutation, useQuery } from "@tanstack/react-query";
import type { VideoSelection } from "@unbox/api";
import { handleError } from "@/api/error";
import { invalidateCampaignQueries } from "@/api/video-campaigns";
import type { ApiLogActionType, ApiTakeActionType } from "@/lib/assurance/action-types";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, useScopedMerchantClient } from "../scoped-merchant-client";

type TakeActionPayload = {
  actionType: ApiTakeActionType;
  selection: VideoSelection;
  notes: string;
  groupTickets?: boolean;
  openCollaborationIds?: string[];
  targetCollaborationIds?: string[];
  campaignIds?: { advertiserId: string; campaignId: string }[];
  sequenceId?: string;
  policyId?: string;
  imageId?: string;
};

type LogActionPayload = {
  actionType: ApiLogActionType;
  selection: VideoSelection;
  notes?: string;
};

export type TakeActionResult =
  | { async: false; count: number; failedCount: number; failedVideoIds: string[]; autoApprovedCount: number }
  | { async: true; jobId: string };

export const invalidateVideoQueries = (merchantId: string, workspaceId: string | null, selection: VideoSelection) => {
  if (selection.mode === "include") {
    for (const videoId of selection.videoIds) {
      queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId, videoId] });
      queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId, workspaceId, videoId] });
    }
  }
  queryClient.invalidateQueries({ queryKey: ["video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["assurance-video", merchantId] });
  queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId, "action-queue"] });
  queryClient.invalidateQueries({ queryKey: ["video", merchantId, workspaceId, "reviewed"] });
};

export const useTakeAction = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: TakeActionPayload): Promise<TakeActionResult> => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions.take.$post({
            param: scope.param,
            json: {
              actionType: payload.actionType,
              selection: payload.selection,
              notes: payload.notes,
              groupTickets: payload.groupTickets ?? false,
              openCollaborationIds: payload.openCollaborationIds ?? [],
              targetCollaborationIds: payload.targetCollaborationIds ?? [],
              campaignIds: payload.campaignIds ?? [],
              sequenceId: payload.sequenceId || undefined,
              policyId: payload.policyId || undefined,
              imageId: payload.imageId,
            },
          })
        : await client.merchant[":merchantId"].assurance.actions.take.$post({
            param: scope.param,
            json: {
              actionType: payload.actionType,
              selection: payload.selection,
              notes: payload.notes,
              groupTickets: payload.groupTickets ?? false,
              openCollaborationIds: payload.openCollaborationIds ?? [],
              targetCollaborationIds: payload.targetCollaborationIds ?? [],
              campaignIds: payload.campaignIds ?? [],
              sequenceId: payload.sequenceId || undefined,
              policyId: payload.policyId || undefined,
              imageId: payload.imageId,
            },
          });

      if (!res.ok) throw await handleError(res);

      const data = await res.json();
      if (res.status === 202 && "async" in data && data.async) {
        return { async: true, jobId: data.jobId };
      }
      if ("count" in data) {
        return {
          async: false,
          count: data.count,
          failedCount: data.failedCount ?? 0,
          failedVideoIds: data.failedVideoIds ?? [],
          autoApprovedCount: data.autoApprovedCount,
        };
      }
      return { async: false, count: 0, failedCount: 0, failedVideoIds: [], autoApprovedCount: 0 };
    },
    onSuccess: (result, variables) => {
      if (scope.merchantId && !result.async) {
        invalidateVideoQueries(scope.merchantId, scope.workspaceId, variables.selection);
        // Synchronous (small/individual) removals must refresh the campaign pill/drawer/overview
        // too — that invalidation used to live only on the async SSE path (UNB-2024).
        if (variables.actionType === "remove_from_ad_campaign") {
          if (variables.selection.mode === "include") {
            for (const videoId of variables.selection.videoIds) {
              invalidateCampaignQueries(scope.merchantId, scope.workspaceId, videoId);
            }
          } else {
            invalidateCampaignQueries(scope.merchantId, scope.workspaceId);
          }
        }
      }
    },
  });
};

const selectionQueryKey = (selection: VideoSelection): string => {
  if (selection.mode === "include") {
    return `include:${selection.videoIds.toSorted().join(",")}`;
  }
  const excludedPart = selection.excludedVideoIds.toSorted().join(",");
  const filterPart = JSON.stringify(selection.filters);
  return `exclude:${excludedPart}:${filterPart}`;
};

export const useUnreviewedCount = (selection: VideoSelection | null) => {
  const scope = useScopedMerchantClient();
  const enabled =
    !!scope.merchantId && selection !== null && (selection.mode === "exclude" || selection.videoIds.length > 0);

  return useQuery({
    queryKey: ["unreviewed-count", scope.merchantId, scope.workspaceId, selection ? selectionQueryKey(selection) : ""],
    queryFn: async () => {
      if (!scope.merchantId || !selection) return { unreviewedCount: 0, unreviewedVideoCount: 0 };

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions["unreviewed-count"].$post({
            param: scope.param,
            json: { selection },
          })
        : await client.merchant[":merchantId"].assurance.actions["unreviewed-count"].$post({
            param: scope.param,
            json: { selection },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled,
  });
};

export const useTemplateContext = (selection: VideoSelection | null) => {
  const scope = useScopedMerchantClient();
  const enabled =
    !!scope.merchantId && selection !== null && (selection.mode === "exclude" || selection.videoIds.length > 0);

  return useQuery({
    queryKey: ["template-context", scope.merchantId, scope.workspaceId, selection ? selectionQueryKey(selection) : ""],
    queryFn: async () => {
      if (!scope.merchantId || !selection) return { shopName: "", creators: [] };

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions["template-context"].$post({
            param: scope.param,
            json: { selection },
          })
        : await client.merchant[":merchantId"].assurance.actions["template-context"].$post({
            param: scope.param,
            json: { selection },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled,
  });
};

export const useLogAction = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: LogActionPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions.log.$post({
            param: scope.param,
            json: {
              actionType: payload.actionType,
              selection: payload.selection,
              notes: payload.notes ?? "",
            },
          })
        : await client.merchant[":merchantId"].assurance.actions.log.$post({
            param: scope.param,
            json: {
              actionType: payload.actionType,
              selection: payload.selection,
              notes: payload.notes ?? "",
            },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: (_, variables) => {
      if (scope.merchantId) {
        invalidateVideoQueries(scope.merchantId, scope.workspaceId, variables.selection);
      }
    },
  });
};

/**
 * Get the active ad campaigns that the selected videos are in.
 * Used to preview what `remove_from_ad_campaign` will affect before executing.
 * Accepts a full VideoSelection so "select all" (exclude mode) previews every affected campaign,
 * not just the videos visible on the current page (UNB-2024).
 */
export const useAffectedAdCampaigns = (selection: VideoSelection | null) => {
  const scope = useScopedMerchantClient();
  const enabled =
    !!scope.merchantId && selection !== null && (selection.mode === "exclude" || selection.videoIds.length > 0);

  return useQuery({
    queryKey: [
      "affected-ad-campaigns",
      scope.merchantId,
      scope.workspaceId,
      selection ? selectionQueryKey(selection) : "",
    ],
    queryFn: async () => {
      if (!scope.merchantId || !selection) return { campaigns: [] };

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions[
            "affected-ad-campaigns"
          ].$post({
            param: scope.param,
            json: { selection },
          })
        : await client.merchant[":merchantId"].assurance.actions["affected-ad-campaigns"].$post({
            param: scope.param,
            json: { selection },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled,
  });
};

/**
 * Get the count of videos with open risks for given creator IDs,
 * optionally filtered by product IDs (from collaborations).
 * Used to show a warning when removing creators from collaborations.
 */
export const useAffectedVideosCount = (creatorIds: string[], productIds: string[] = []) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: [
      "affected-videos-count",
      scope.merchantId,
      scope.workspaceId,
      creatorIds.toSorted().join(","),
      productIds.toSorted().join(","),
    ],
    queryFn: async () => {
      if (!scope.merchantId || creatorIds.length === 0) return { affectedVideoCount: 0 };

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].assurance.actions[
            "affected-videos-count"
          ].$post({
            param: scope.param,
            json: { creatorIds, productIds },
          })
        : await client.merchant[":merchantId"].assurance.actions["affected-videos-count"].$post({
            param: scope.param,
            json: { creatorIds, productIds },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && creatorIds.length > 0,
  });
};
